import { useEffect, useState } from 'react';
import { useApi } from '../../services/api';
import { Invitation } from '@bst-stellar/shared';
import { formatDate } from '../../utils/dateFormatter';

export default function InvitationsPage() {
    const api = useApi();
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        loadInvitations();
    }, []);

    async function loadInvitations() {
        try {
            setLoading(true);
            const result = await api.getInvitations();
            setInvitations(result.invitations || []);
        } catch (error) {
            console.error('Failed to load invitations:', error);
        } finally {
            setLoading(false);
        }
    }

    async function handleSendInvitation() {
        if (!email.trim()) return;

        try {
            setSending(true);
            setError('');
            await api.createInvitation(email.trim());
            setEmail('');
            await loadInvitations();
        } catch (error: any) {
            setError(error.message || 'Failed to send invitation');
        } finally {
            setSending(false);
        }
    }

    async function handleRemind(id: string) {
        try {
            await api.sendInvitationReminder(id);
            await loadInvitations();
        } catch (error) {
            console.error('Failed to send reminder:', error);
        }
    }

    async function handleDelete(id: string) {
        if (!confirm('Are you sure you want to delete this invitation?')) return;

        try {
            await api.deleteInvitation(id);
            await loadInvitations();
        } catch (error) {
            console.error('Failed to delete invitation:', error);
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'accepted': return 'bg-green-100 text-green-800';
            case 'expired': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return <div className="text-center py-12 text-text-secondary">Loading invitations...</div>;
    }

    return (
        <div>
            <h1 className="text-3xl font-bold text-text-primary mb-6">User Invitations</h1>

            {/* Send Invitation Form */}
            <div className="bg-surface rounded-lg shadow p-6 mb-6 border border-border">
                <h2 className="text-lg font-semibold text-text-primary mb-4">Invite a User</h2>
                <p className="text-sm text-text-secondary mb-4">
                    Invited users can sign up immediately without waiting for approval.
                </p>

                <div className="flex gap-3">
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter email address"
                        className="flex-1 px-4 py-2 border border-border rounded-lg bg-background text-text-primary focus:ring-2 focus:ring-primary focus:border-transparent"
                        onKeyDown={(e) => e.key === 'Enter' && handleSendInvitation()}
                    />
                    <button
                        onClick={handleSendInvitation}
                        disabled={sending || !email.trim()}
                        className="px-6 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {sending ? 'Sending...' : 'Send Invitation'}
                    </button>
                </div>

                {error && (
                    <p className="text-red-500 text-sm mt-2">{error}</p>
                )}
            </div>

            {/* Invitations List */}
            <div className="bg-surface rounded-lg shadow border border-border">
                <div className="p-4 border-b border-border">
                    <h2 className="text-lg font-semibold text-text-primary">
                        Pending Invitations ({invitations.filter(i => i.status === 'pending').length})
                    </h2>
                </div>

                {invitations.length === 0 ? (
                    <div className="p-12 text-center text-text-secondary">
                        No invitations yet. Send your first invitation above.
                    </div>
                ) : (
                    <div className="divide-y divide-border">
                        {invitations.map((invitation) => (
                            <div key={invitation.id} className="p-4 flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-text-primary">{invitation.email}</p>
                                    <div className="flex items-center gap-3 text-sm text-text-secondary mt-1">
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(invitation.status)}`}>
                                            {invitation.status.toUpperCase()}
                                        </span>
                                        <span>
                                            Sent {formatDate(invitation.createdAt)}
                                        </span>
                                        {invitation.remindersSent > 0 && (
                                            <span>• {invitation.remindersSent} reminder(s) sent</span>
                                        )}
                                        {invitation.status === 'pending' && (
                                            <span>• Expires {formatDate(invitation.expiresAt)}</span>
                                        )}
                                    </div>
                                </div>

                                {invitation.status === 'pending' && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleRemind(invitation.id)}
                                            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                        >
                                            Send Reminder
                                        </button>
                                        <button
                                            onClick={() => handleDelete(invitation.id)}
                                            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
